// schwab-20261103: options chain for a Schwab underlying (data only, no trading).
// Polls /stk/chain every 15s via our server (which asks the Corsair relay).
// Clicking a contract charts it as STK-OPT:<schwab option symbol>.
import { useEffect, useState } from 'react'

const BASE = import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'
const f = (v, d = 2) => (typeof v === 'number' && isFinite(v) ? v.toFixed(d) : '—')

const CSS = `
.opt-panel{display:flex;flex-direction:column;height:100%;width:100%;background:var(--surface);color:var(--text-hi);font-size:11px;font-variant-numeric:tabular-nums;min-height:0}
.opt-head{display:flex;align-items:center;gap:6px;height:var(--rail-h);padding:0 8px;background:var(--surface-rail);border-bottom:1px solid var(--seam);flex-shrink:0}
.opt-title{color:var(--text-mid);letter-spacing:.08em;font-weight:600;margin-right:auto;white-space:nowrap}
.opt-dim{color:var(--text-lo);font-size:10px;white-space:nowrap}
.opt-sel{background:none;border:1px solid var(--seam);color:var(--text-mid);font:inherit;font-size:10px;padding:1px 4px}
.opt-sel option{background:#000}
.opt-body{flex:1;overflow:auto;min-height:0;scrollbar-color:#000 #000;scrollbar-width:thin}.opt-body::-webkit-scrollbar{width:6px;height:6px;background:#000}.opt-body::-webkit-scrollbar-thumb{background:#000}.opt-body::-webkit-scrollbar-track{background:#000}.opt-body::-webkit-scrollbar-corner{background:#000}
.opt-tbl{width:100%;border-collapse:collapse;text-align:right}
.opt-tbl th{position:sticky;top:0;background:var(--surface-rail);color:var(--text-lo);font-size:9px;letter-spacing:.06em;font-weight:400;padding:2px 4px;border-bottom:1px solid var(--seam)}
.opt-tbl td{padding:2px 4px;border-bottom:1px solid var(--seam);color:var(--text-mid)}
.opt-tbl td.opt-c{cursor:pointer}.opt-tbl td.opt-c:hover{color:var(--text-hi)}
.opt-tbl td.itm{background:var(--surface-active)}
.opt-tbl td.on{color:var(--accent);box-shadow:inset 0 -1px 0 var(--accent)}
.opt-k{text-align:center !important;color:var(--text-hi) !important;font-weight:600}
.opt-msg{padding:8px;color:var(--text-lo)}
`

export default function OptionsChainPanel({ underlying, onPick, activeCoin, relayUp }) {
  const [exp, setExp] = useState('')
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [exps, setExps] = useState([])
  // optstrikes-20261104: strikes per side, saved
  const [nK, setNK] = useState(() => +localStorage.getItem('solcheddar:optStrikes') || 12)
  useEffect(() => { localStorage.setItem('solcheddar:optStrikes', String(nK)) }, [nK])
  useEffect(() => { setExp(''); setData(null); setExps([]) }, [underlying])
  useEffect(() => {
    let dead = false
    const load = async () => {
      try {
        const q = `symbol=${encodeURIComponent(underlying)}&strikes=${nK * 2}${exp ? `&expiry=${exp}` : ''}`
        const r = await fetch(`${BASE}/stk/chain?${q}`)
        const j = await r.json()
        if (dead) return
        if (!r.ok || j.error) { setErr(j.error || `HTTP ${r.status}`); return }
        setErr(''); setData(j)
        if (!exp && j.expiries?.length) { setExps(j.expiries); setExp(j.expiries[0]) }
      } catch (e) { if (!dead) setErr('Schwab relay offline') }
    }
    load()
    const t = setInterval(load, 15000)
    return () => { dead = true; clearInterval(t) }
  }, [underlying, exp, nK])

  const calls = (data?.calls || []).filter(c => !exp || c.exp === exp)
  const puts = (data?.puts || []).filter(c => !exp || c.exp === exp)
  const strikes = [...new Set([...calls, ...puts].map(c => c.strikePrice))].sort((a, b) => a - b)
  const byK = (arr) => Object.fromEntries(arr.map(c => [c.strikePrice, c]))
  const C = byK(calls), P = byK(puts)
  const spot = typeof data?.underlying === 'number' ? data.underlying : null
  const cell = (c, itm) => {
    if (!c) return <td colSpan={4} />
    const on = activeCoin === 'STK-OPT:' + c.symbol
    const cls = `opt-c${itm ? ' itm' : ''}${on ? ' on' : ''}`
    const go = () => onPick(c)
    return <>
      <td className={cls} onClick={go}>{f(c.bid)}</td><td className={cls} onClick={go}>{f(c.ask)}</td>
      <td className={cls} onClick={go}>{c.totalVolume ?? '—'}</td><td className={cls} onClick={go}>{f(c.delta)}</td>
    </>
  }
  return (
    <div className="opt-panel">
      <style>{CSS}</style>
      <div className="opt-head">
        <span className="opt-title">OPTIONS · {underlying}</span>
        {spot != null && <span className="opt-dim">spot {f(spot)}</span>}
        <select className="opt-sel" value={nK} onChange={e => setNK(+e.target.value)} title="Strikes each side">
          {[5, 10, 12, 20, 30, 50].map(n => <option key={n} value={n}>{n} each</option>)}
        </select>
        <select className="opt-sel" value={exp} onChange={e => setExp(e.target.value)}>
          {exps.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div className="opt-body">
        {(err || relayUp === false) && <div className="opt-msg" style={{ color: 'var(--down)' }}>{err || 'Schwab relay offline'}</div>}
        {!data && !err && <div className="opt-msg">Loading…</div>}
        <table className="opt-tbl">
          <thead><tr>
            <th colSpan={4} style={{ textAlign: 'center' }}>CALLS · bid ask vol Δ</th><th>STRIKE</th><th colSpan={4} style={{ textAlign: 'center' }}>PUTS · bid ask vol Δ</th>
          </tr></thead>
          <tbody>{strikes.map(k => (
            <tr key={k}>
              {cell(C[k], spot != null && k < spot)}
              <td className="opt-k">{k}</td>
              {cell(P[k], spot != null && k > spot)}
            </tr>))}</tbody>
        </table>
        <div className="opt-msg">view only · click a contract to chart it · follows the charted stock</div>
      </div>
    </div>
  )
}
